import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Building2, Users, IndianRupee, TrendingUp, Shield, Search,
  Power, PowerOff, Trash2, Key, AlertTriangle, Clock, Crown,
  LogOut, Eye, Mail, Phone, MapPin, Calendar, Receipt, User, X, Database
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, value, label, bgColor, textColor }) {
  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${bgColor}`}>
            <Icon className={`w-6 h-6 ${textColor}`} />
          </div>
          <div>
            <p className="text-2xl font-bold text-white">{value}</p>
            <p className="text-sm text-slate-400">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AdminDashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTenant, setSearchTenant] = useState('');
  const [searchUser, setSearchUser] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  
  // Modal states
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTenantDetail, setShowTenantDetail] = useState(false);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [modalType, setModalType] = useState(''); // 'tenant' or 'user'
  const [newPlan, setNewPlan] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const fetchData = useCallback(async function() {
    try {
      const [statsRes, tenantsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/tenants`),
        axios.get(`${API}/admin/users`)
      ]);
      setStats(statsRes.data);
      setTenants(tenantsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Platform admin privileges required.');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(function() { fetchData(); }, [fetchData]);

  async function toggleTenantStatus(tenant) {
    try {
      await axios.put(`${API}/admin/tenants/${tenant.id}/status`, {
        is_active: !tenant.is_active
      });
      toast.success(tenant.is_active ? 'Tenant disabled' : 'Tenant enabled');
      fetchData();
    } catch (e) {
      toast.error('Failed to update tenant status');
    }
  }

  async function toggleUserStatus(user) {
    try {
      await axios.put(`${API}/admin/users/${user.id}/status`, {
        is_active: !user.is_active
      });
      toast.success(user.is_active ? 'User disabled' : 'User enabled');
      fetchData();
    } catch (e) {
      toast.error('Failed to update user status');
    }
  }

  async function updatePlan() {
    if (!selectedItem || !newPlan) return;
    try {
      await axios.put(`${API}/admin/tenants/${selectedItem.id}/plan`, {
        plan: newPlan
      });
      toast.success(`Plan updated to ${newPlan}`);
      setShowPlanModal(false);
      setSelectedItem(null);
      setNewPlan('');
      fetchData();
    } catch (e) {
      toast.error('Failed to update plan');
    }
  }

  async function resetPassword() {
    if (!selectedItem || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.put(`${API}/admin/users/${selectedItem.id}/password`, {
        new_password: newPassword
      });
      toast.success('Password reset successfully');
      setShowPasswordModal(false);
      setSelectedItem(null);
      setNewPassword('');
    } catch (e) {
      toast.error('Failed to reset password');
    }
  }

  async function deleteItem() {
    if (!selectedItem) return;
    try {
      if (modalType === 'tenant') {
        await axios.delete(`${API}/admin/tenants/${selectedItem.id}`);
        toast.success('Tenant deleted permanently');
        setShowTenantDetail(false);
      } else {
        await axios.delete(`${API}/admin/users/${selectedItem.id}`);
        toast.success('User deleted permanently');
        setShowUserDetail(false);
      }
      setShowDeleteModal(false);
      setSelectedItem(null);
      setModalType('');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  }

  function handleLogout() {
    logout();
    navigate('/login');
    toast.success('Logged out successfully');
  }

  async function openTenantDetail(tenant) {
    setSelectedTenant(tenant);
    setShowTenantDetail(true);
    // Fetch users for this tenant
    try {
      const res = await axios.get(`${API}/admin/users?tenant_id=${tenant.id}`);
      setTenantUsers(res.data);
    } catch (e) {
      setTenantUsers([]);
    }
  }

  function openUserDetail(user) {
    setSelectedUser(user);
    setShowUserDetail(true);
  }

  function openPlanModal(tenant) {
    setSelectedItem(tenant);
    setNewPlan(tenant.plan);
    setShowPlanModal(true);
  }

  function openPasswordModal(user) {
    setSelectedItem(user);
    setNewPassword('');
    setShowPasswordModal(true);
  }

  function openDeleteModal(item, type) {
    setSelectedItem(item);
    setModalType(type);
    setShowDeleteModal(true);
  }

  // Filter tenants
  const filteredTenants = tenants.filter(function(t) {
    const matchesSearch = !searchTenant || 
      t.shop_name.toLowerCase().includes(searchTenant.toLowerCase()) ||
      t.owner_name.toLowerCase().includes(searchTenant.toLowerCase());
    const matchesPlan = filterPlan === 'all' || t.plan === filterPlan;
    return matchesSearch && matchesPlan;
  });

  // Filter users
  const filteredUsers = users.filter(function(u) {
    return !searchUser || 
      u.username.toLowerCase().includes(searchUser.toLowerCase()) ||
      u.shop_name.toLowerCase().includes(searchUser.toLowerCase());
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-purple-400" />
            <div>
              <h1 className="text-2xl font-bold text-white">Platform Admin Dashboard</h1>
              <p className="text-slate-400 text-sm">Manage all tenants, users, and subscriptions</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="border-purple-500 text-purple-400 hover:bg-purple-900/50"
              onClick={function() { navigate('/admin/medicines'); }}
              data-testid="admin-medicines-btn"
            >
              <Database className="w-4 h-4 mr-2" />
              Medicine Database
            </Button>
            <Button
              variant="outline"
              className="border-red-600 text-red-400 hover:bg-red-900/50"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard 
            icon={Building2} 
            value={stats.total_tenants} 
            label="Total Pharmacies" 
            bgColor="bg-blue-900" 
            textColor="text-blue-400" 
          />
          <StatCard 
            icon={Users} 
            value={stats.total_users} 
            label="Total Users" 
            bgColor="bg-green-900" 
            textColor="text-green-400" 
          />
          <StatCard 
            icon={IndianRupee} 
            value={`₹${stats.total_platform_revenue.toLocaleString()}`} 
            label="Platform Revenue" 
            bgColor="bg-amber-900" 
            textColor="text-amber-400" 
          />
          <StatCard 
            icon={Clock} 
            value={stats.expiring_trials_7_days} 
            label="Trials Expiring (7d)" 
            bgColor="bg-red-900" 
            textColor="text-red-400" 
          />
        </div>
      )}

      {/* Plan Distribution */}
      {stats && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-400">{stats.plans.free_trial}</p>
              <p className="text-sm text-slate-400">Free Trial</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-400">{stats.plans.pro}</p>
              <p className="text-sm text-slate-400">Pro Plan</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-400">{stats.plans.pro_plus}</p>
              <p className="text-sm text-slate-400">Pro Plus</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="tenants" className="space-y-4">
        <TabsList className="bg-slate-800 border-slate-700">
          <TabsTrigger value="tenants" className="data-[state=active]:bg-slate-700">
            <Building2 className="w-4 h-4 mr-2" />
            Pharmacies ({tenants.length})
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-slate-700">
            <Users className="w-4 h-4 mr-2" />
            Users ({users.length})
          </TabsTrigger>
        </TabsList>

        {/* Tenants Tab */}
        <TabsContent value="tenants">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-3">
              <div className="flex flex-col md:flex-row gap-3 justify-between">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    placeholder="Search by shop name or owner..."
                    value={searchTenant}
                    onChange={function(e) { setSearchTenant(e.target.value); }}
                    className="pl-10 bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <Select value={filterPlan} onValueChange={setFilterPlan}>
                  <SelectTrigger className="w-40 bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Plans</SelectItem>
                    <SelectItem value="free_trial">Free Trial</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="pro_plus">Pro Plus</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {filteredTenants.map(function(tenant) {
                    return (
                      <div 
                        key={tenant.id} 
                        className={`p-4 rounded-lg border ${tenant.is_active ? 'bg-slate-700 border-slate-600' : 'bg-red-900/20 border-red-800'}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-white">{tenant.shop_name}</h3>
                              {!tenant.is_active && (
                                <Badge className="bg-red-600 text-white text-xs">Disabled</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-400">Owner: {tenant.owner_name}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span>Users: {tenant.users_count}</span>
                              <span>Bills: {tenant.total_bills}</span>
                              <span>Revenue: ₹{tenant.total_revenue.toLocaleString()}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-blue-500 text-blue-400 hover:bg-blue-900/30"
                              onClick={function() { openTenantDetail(tenant); }}
                              data-testid={`view-tenant-${tenant.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            <Badge 
                              className={
                                tenant.plan === 'pro_plus' ? 'bg-purple-600' :
                                tenant.plan === 'pro' ? 'bg-blue-600' : 'bg-amber-600'
                              }
                              onClick={function() { openPlanModal(tenant); }}
                              style={{ cursor: 'pointer' }}
                            >
                              <Crown className="w-3 h-3 mr-1" />
                              {tenant.plan}
                            </Badge>
                            <Button
                              size="sm"
                              variant={tenant.is_active ? "outline" : "default"}
                              className={tenant.is_active ? "border-slate-500 text-slate-300" : "bg-green-600"}
                              onClick={function() { toggleTenantStatus(tenant); }}
                            >
                              {tenant.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={function() { openDeleteModal(tenant, 'tenant'); }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {filteredTenants.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      No pharmacies found
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-3">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Search by username or shop..."
                  value={searchUser}
                  onChange={function(e) { setSearchUser(e.target.value); }}
                  className="pl-10 bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {filteredUsers.map(function(user) {
                    return (
                      <div 
                        key={user.id} 
                        className={`p-4 rounded-lg border ${user.is_active ? 'bg-slate-700 border-slate-600' : 'bg-red-900/20 border-red-800'}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-white">{user.username}</h3>
                              {!user.is_active && (
                                <Badge className="bg-red-600 text-white text-xs">Disabled</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-400">{user.shop_name}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span>Plan: {user.plan}</span>
                              <span>Last Login: {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-blue-500 text-blue-400 hover:bg-blue-900/30"
                              onClick={function() { openUserDetail(user); }}
                              data-testid={`view-user-${user.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-slate-500 text-slate-300"
                              onClick={function() { openPasswordModal(user); }}
                            >
                              <Key className="w-4 h-4 mr-1" />
                              Reset
                            </Button>
                            <Button
                              size="sm"
                              variant={user.is_active ? "outline" : "default"}
                              className={user.is_active ? "border-slate-500 text-slate-300" : "bg-green-600"}
                              onClick={function() { toggleUserStatus(user); }}
                            >
                              {user.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={function() { openDeleteModal(user, 'user'); }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {filteredUsers.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      No users found
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Plan Update Modal */}
      <Dialog open={showPlanModal} onOpenChange={setShowPlanModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Update Subscription Plan</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <p className="text-slate-400">
                Updating plan for: <strong className="text-white">{selectedItem.shop_name}</strong>
              </p>
              <div>
                <Label className="text-slate-300">Select Plan</Label>
                <Select value={newPlan} onValueChange={setNewPlan}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free_trial">Free Trial (50 customers/month, 15 days)</SelectItem>
                    <SelectItem value="pro">Pro (999 customers/month, ₹999/month)</SelectItem>
                    <SelectItem value="pro_plus">Pro Plus (Unlimited, ₹9199/month)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={function() { setShowPlanModal(false); }}>Cancel</Button>
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={updatePlan}>Update Plan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Reset User Password</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <p className="text-slate-400">
                Resetting password for: <strong className="text-white">{selectedItem.username}</strong>
              </p>
              <div>
                <Label className="text-slate-300">New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={function(e) { setNewPassword(e.target.value); }}
                  placeholder="Enter new password (min 6 chars)"
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={function() { setShowPasswordModal(false); }}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={resetPassword}>Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Confirm Permanent Deletion
            </DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <p className="text-slate-300">
                Are you sure you want to permanently delete this {modalType}?
              </p>
              <div className="p-3 bg-red-900/30 rounded-lg border border-red-800">
                <p className="font-semibold text-white">
                  {modalType === 'tenant' ? selectedItem.shop_name : selectedItem.username}
                </p>
                {modalType === 'tenant' && (
                  <p className="text-sm text-red-400 mt-1">
                    This will delete ALL associated data including users, medicines, bills, customers, and doctors.
                  </p>
                )}
              </div>
              <p className="text-sm text-amber-400">
                ⚠️ This action cannot be undone!
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={function() { setShowDeleteModal(false); }}>Cancel</Button>
            <Button variant="destructive" onClick={deleteItem}>Delete Permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tenant Detail Modal */}
      <Dialog open={showTenantDetail} onOpenChange={setShowTenantDetail}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-400" />
              Tenant Details
            </DialogTitle>
          </DialogHeader>
          {selectedTenant && (
            <div className="space-y-4">
              {/* Tenant Info */}
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-white">{selectedTenant.shop_name}</h3>
                  <Badge 
                    className={
                      selectedTenant.plan === 'pro_plus' ? 'bg-purple-600' :
                      selectedTenant.plan === 'pro' ? 'bg-blue-600' : 'bg-amber-600'
                    }
                  >
                    <Crown className="w-3 h-3 mr-1" />
                    {selectedTenant.plan}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-slate-300">
                    <User className="w-4 h-4 text-slate-500" />
                    <span>Owner: {selectedTenant.owner_name || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <Mail className="w-4 h-4 text-slate-500" />
                    <span>{selectedTenant.email || 'No email'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <Phone className="w-4 h-4 text-slate-500" />
                    <span>{selectedTenant.phone || 'No phone'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    <span>Created: {new Date(selectedTenant.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex gap-4 mt-3 pt-3 border-t border-slate-600 text-sm">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-400" />
                    <span className="text-slate-300">{selectedTenant.users_count} Users</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-green-400" />
                    <span className="text-slate-300">{selectedTenant.total_bills} Bills</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <IndianRupee className="w-4 h-4 text-amber-400" />
                    <span className="text-slate-300">₹{selectedTenant.total_revenue.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Users List */}
              <div>
                <h4 className="text-sm font-semibold text-slate-400 mb-2">Users ({tenantUsers.length})</h4>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-2">
                    {tenantUsers.map(function(user) {
                      return (
                        <div key={user.id} className="p-3 bg-slate-700 rounded-lg flex items-center justify-between">
                          <div>
                            <p className="font-medium text-white">{user.username}</p>
                            <p className="text-xs text-slate-400">
                              Last login: {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {!user.is_active && <Badge className="bg-red-600 text-xs">Disabled</Badge>}
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-slate-500 text-slate-300 h-7 text-xs"
                              onClick={function() { 
                                setShowTenantDetail(false);
                                openPasswordModal(user); 
                              }}
                            >
                              <Key className="w-3 h-3 mr-1" />
                              Reset Password
                            </Button>
                            <Button
                              size="sm"
                              variant={user.is_active ? "outline" : "default"}
                              className={user.is_active ? "border-slate-500 text-slate-300 h-7" : "bg-green-600 h-7"}
                              onClick={function() { toggleUserStatus(user); }}
                            >
                              {user.is_active ? <PowerOff className="w-3 h-3" /> : <Power className="w-3 h-3" />}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                    {tenantUsers.length === 0 && (
                      <p className="text-center text-slate-500 py-4">No users found for this tenant</p>
                    )}
                  </div>
                </ScrollArea>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t border-slate-700">
                <Button
                  variant="outline"
                  className="flex-1 border-purple-500 text-purple-400"
                  onClick={function() { 
                    setShowTenantDetail(false);
                    openPlanModal(selectedTenant); 
                  }}
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Change Plan
                </Button>
                <Button
                  variant={selectedTenant.is_active ? "outline" : "default"}
                  className={selectedTenant.is_active ? "flex-1 border-amber-500 text-amber-400" : "flex-1 bg-green-600"}
                  onClick={function() { 
                    toggleTenantStatus(selectedTenant);
                    setShowTenantDetail(false);
                  }}
                >
                  {selectedTenant.is_active ? <PowerOff className="w-4 h-4 mr-2" /> : <Power className="w-4 h-4 mr-2" />}
                  {selectedTenant.is_active ? 'Disable Tenant' : 'Enable Tenant'}
                </Button>
                <Button
                  variant="destructive"
                  onClick={function() { 
                    setShowTenantDetail(false);
                    openDeleteModal(selectedTenant, 'tenant'); 
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* User Detail Modal */}
      <Dialog open={showUserDetail} onOpenChange={setShowUserDetail}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-green-400" />
              User Details
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              {/* User Info */}
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-white">{selectedUser.username}</h3>
                  <div className="flex items-center gap-2">
                    {!selectedUser.is_active && <Badge className="bg-red-600">Disabled</Badge>}
                    <Badge className={selectedUser.plan === 'pro_plus' ? 'bg-purple-600' : selectedUser.plan === 'pro' ? 'bg-blue-600' : 'bg-amber-600'}>
                      {selectedUser.plan}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-slate-300">
                    <Building2 className="w-4 h-4 text-slate-500" />
                    <span>Shop: {selectedUser.shop_name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    <span>Created: {new Date(selectedUser.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <Clock className="w-4 h-4 text-slate-500" />
                    <span>Last Login: {selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString() : 'Never'}</span>
                  </div>
                </div>
              </div>

              {/* Credentials Section */}
              <div className="p-4 bg-blue-900/20 rounded-lg border border-blue-800">
                <h4 className="text-sm font-semibold text-blue-400 mb-2">Login Credentials</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Username:</span>
                    <code className="bg-slate-700 px-2 py-1 rounded text-white">{selectedUser.username}</code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Password:</span>
                    <span className="text-slate-500 italic">Hidden for security</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t border-slate-700">
                <Button
                  variant="outline"
                  className="flex-1 border-blue-500 text-blue-400"
                  onClick={function() { 
                    setShowUserDetail(false);
                    openPasswordModal(selectedUser); 
                  }}
                >
                  <Key className="w-4 h-4 mr-2" />
                  Reset Password
                </Button>
                <Button
                  variant={selectedUser.is_active ? "outline" : "default"}
                  className={selectedUser.is_active ? "flex-1 border-amber-500 text-amber-400" : "flex-1 bg-green-600"}
                  onClick={function() { 
                    toggleUserStatus(selectedUser);
                    setShowUserDetail(false);
                  }}
                >
                  {selectedUser.is_active ? <PowerOff className="w-4 h-4 mr-2" /> : <Power className="w-4 h-4 mr-2" />}
                  {selectedUser.is_active ? 'Disable' : 'Enable'}
                </Button>
                <Button
                  variant="destructive"
                  onClick={function() { 
                    setShowUserDetail(false);
                    openDeleteModal(selectedUser, 'user'); 
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminDashboard;
